// Globals
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';

// Components
import { StyledDragger, StyledUploadSelect } from './FileUploadStyles';
import { showErrorToast } from '@/components';

// Helpers
import { validateFileSize } from '@/utils';

export type TFileUpload = {
  disabled?: boolean;
  onChange?: (value: File, fileList: File[]) => void;
  acceptance?: string;
  maxFileSize?: number;
  children?: ReactNode;
  maxCount?: number;
  showList?: boolean;
  multiple?: boolean;
  type?: 'select' | 'drag';
};

// workaround due to restrictions of antd
// https://github.com/ant-design/ant-design/issues/8579
let currentFlushPromise: Promise<void> | null = null;
const queueFlush = (fn: () => void) => {
  if (!currentFlushPromise) {
    currentFlushPromise = Promise.resolve().then(() => {
      fn();
      currentFlushPromise = null;
    });
  }
};

const FileUpload = ({
  onChange,
  acceptance,
  maxFileSize,
  disabled,
  maxCount,
  showList,
  children,
  multiple,
  type,
}: TFileUpload) => {
  const { t } = useTranslation('common');

  const handleChange = (file: File, fileList: File[]) => {
    if (acceptance && !fileList.every((f) => acceptance.includes(f.type))) {
      showErrorToast({
        message: t('error_file_format'),
      });
      return false;
    }

    if (maxFileSize && !fileList.every((f) => validateFileSize(f.size, maxFileSize))) {
      showErrorToast({
        message: t('error_file_size', { limit: maxFileSize / 1024 / 1024 }),
      });
      return false;
    }

    onChange?.(file, fileList);
    return false;
  };

  const beforeUpload = (file: File, fileList: File[]) => {
    queueFlush(() => handleChange(file, fileList));
    return false;
  };

  if (type === 'select') {
    return (
      <StyledUploadSelect
        maxCount={maxCount}
        showUploadList={showList}
        multiple={multiple}
        fileList={[]}
        accept={acceptance}
        disabled={disabled}
        beforeUpload={beforeUpload}
        type="drag"
      >
        {children}
      </StyledUploadSelect>
    );
  }

  return (
    <StyledDragger
      maxCount={maxCount}
      showUploadList={showList}
      multiple={multiple}
      accept={acceptance}
      disabled={disabled}
      beforeUpload={beforeUpload}
      fileList={[]}
    >
      {children}
    </StyledDragger>
  );
};

export default FileUpload;
